import React, { useMemo, useState } from 'react';
import {
  BrioModal, BrioModalBody, BrioModalField, BrioInput, BrioSelect, BrioButton, BrioDatePicker,
} from '../../components/BrioComponents';
import { MEASURE_BASIS_LABELS } from '../utils/sdRateStats';

// Manually add an SD area-rate to the corpus. Subtrade options come from the
// brio_trade taxonomy (value = classification_nodes.id = subtrade_node_id).
// Writes are restricted to admin/supervisor by RLS on boq.sd_rates.
function AddSdRateModal({ taxonomies, typologies = [], onClose, onCreate }) {
  const [form, setForm] = useState({
    subtradeNodeId: '',
    measureBasis: 'built_up_area',
    rate: '',
    rateDate: new Date().toISOString().slice(0, 10),
    typologyId: '',
    zoneLabel: '',
    city: '',
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Subtrades grouped by their parent trade, for an <optgroup> picker.
  const tradeGroups = useMemo(() => {
    const brio = (taxonomies || []).find((t) => t.code === 'brio_trade');
    if (!brio) return [];
    const byId = new Map(brio.nodes.map((n) => [n.id, n]));
    const groups = new Map();
    brio.nodes
      .filter((n) => n.parentId)
      .forEach((n) => {
        const trade = byId.get(n.parentId)?.label || 'Other';
        if (!groups.has(trade)) groups.set(trade, []);
        groups.get(trade).push(n);
      });
    return [...groups.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([trade, nodes]) => [trade, nodes.sort((a, b) => a.label.localeCompare(b.label))]);
  }, [taxonomies]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.subtradeNodeId) { setError('Pick a subtrade.'); return; }
    const rate = Number(form.rate);
    if (!Number.isFinite(rate) || rate <= 0) { setError('Enter a positive rate (₹/sqm).'); return; }
    if (!form.rateDate) { setError('Pick a rate date.'); return; }
    setSubmitting(true);
    const res = await onCreate([{
      subtradeNodeId: form.subtradeNodeId,
      measureBasis: form.measureBasis,
      rate,
      rateDate: form.rateDate,
      currency: 'INR',
      region: 'IN',
      typologyId: form.typologyId || null,
      zoneLabel: form.zoneLabel,
      city: form.city,
      note: form.note,
      source: 'manual',
    }]);
    setSubmitting(false);
    if (!res?.success) setError(res?.error || 'Failed to add rate.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Saving…' : 'Add rate'}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title="Add SD area-rate" onClose={onClose} width="sm" footer={footer}>
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <BrioModalField label="Subtrade *">
            <BrioSelect value={form.subtradeNodeId} onChange={update('subtradeNodeId')} autoFocus>
              <option value="">Select a subtrade…</option>
              {tradeGroups.map(([trade, nodes]) => (
                <optgroup key={trade} label={trade}>
                  {nodes.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
                </optgroup>
              ))}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField label="Measure basis *" hint="The building area the rate is expressed per.">
            <BrioSelect value={form.measureBasis} onChange={update('measureBasis')}>
              {Object.entries(MEASURE_BASIS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField label="Rate (₹/sqm) *">
            <BrioInput type="number" step="any" value={form.rate} onChange={update('rate')} placeholder="e.g. 1200" />
          </BrioModalField>
          <BrioModalField label="Rate date *" hint="Pricing date of the estimate — used to escalate the rate.">
            <BrioDatePicker value={form.rateDate} onChange={(v) => setForm((f) => ({ ...f, rateDate: v || '' }))} clearable={false} />
          </BrioModalField>
          <BrioModalField label="Typology" hint="Building typology this rate represents.">
            <BrioSelect value={form.typologyId} onChange={update('typologyId')}>
              <option value="">— None —</option>
              {typologies.filter((t) => t.isActive).map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField label="Zone" hint="Optional — block/zone this rate came from (e.g. Main House).">
            <BrioInput value={form.zoneLabel} onChange={update('zoneLabel')} placeholder="e.g. Main House" />
          </BrioModalField>
          <BrioModalField label="City" hint="Optional.">
            <BrioInput value={form.city} onChange={update('city')} placeholder="e.g. Mumbai" />
          </BrioModalField>
          <BrioModalField label="Note" hint="Optional — source / context for this rate.">
            <BrioInput value={form.note} onChange={update('note')} placeholder="e.g. SD budget, May 2026" />
          </BrioModalField>
        </form>
      </BrioModalBody>
    </BrioModal>
  );
}

export default AddSdRateModal;
